import { Controller } from "@hotwired/stimulus";
import { toastManager } from "../components/DownloadTray/ToastManager";

/**
 * Invitees Controller
 *
 * Handles adding/removing invitees from the event request wizard.
 * Supports keyboard shortcuts (Enter to add) and duplicate detection.
 */
export default class extends Controller {
  static targets = ["form", "email", "firstName", "lastName", "list", "unsavedIndicator"];
  static values = { eventDays: { type: Array, default: [] } };

  connect() {
    // Listen for auto-add events from Azure AD lookup
    this.boundHandleAutoAdd = this.handleAutoAdd.bind(this);
    this.element.addEventListener("azure-ad:auto-add", this.boundHandleAutoAdd);

    // GR-2566 — the bulk-add flow appends persisted rows in place (no reload)
    // and asks us to resync the counter and empty state.
    this.boundRefresh = this.refresh.bind(this);
    this.element.addEventListener("invitees:refresh", this.boundRefresh);

    // Clear validation error state as soon as the user starts typing,
    // so stale errors don't carry over after the offending field is fixed.
    this.boundClearOnInput = this.clearOnInput.bind(this);
    this.editableInputs().forEach((target) =>
      target.addEventListener("input", this.boundClearOnInput)
    );

    // GR-1789 #27 — guard against silent data loss. Step 7 deliberately
    // does NOT autosave (per event_request_wizard_controller.js:8 contract),
    // so an invitee added but not "Save & Continue"d is lost on nav-away.
    // Track dirty state and warn before unload / Turbo nav.
    this.dirty = false;
    this.boundBeforeUnload = this.handleBeforeUnload.bind(this);
    this.boundBeforeVisit = this.handleBeforeVisit.bind(this);
    // turbo:submit-end fires after the server has responded with success or
    // failure — only clear dirty on success so a rejected save keeps the
    // safety net armed for the user's subsequent edits.
    this.boundSubmitEnd = this.handleSubmitEnd.bind(this);
    window.addEventListener("beforeunload", this.boundBeforeUnload);
    document.addEventListener("turbo:before-visit", this.boundBeforeVisit);
    this.wizardForm = this.element.closest("form");
    if (this.wizardForm) {
      this.wizardForm.addEventListener("turbo:submit-end", this.boundSubmitEnd);
    }

    // GR-1789 #27 — re-arm after a server-side validation failure. When the
    // controller re-renders :edit with :unprocessable_entity, Stimulus
    // disconnects/reconnects with dirty=false but the rejected invitee rows
    // are still in the DOM (new records with no id), so navigating away
    // would silently drop them. If any invitee row is missing a persisted
    // id on connect, treat the form as dirty from the start.
    if (this.hasUnpersistedInviteeRows()) this.markDirty();
  }

  disconnect() {
    this.element.removeEventListener(
      "azure-ad:auto-add",
      this.boundHandleAutoAdd
    );
    this.element.removeEventListener("invitees:refresh", this.boundRefresh);
    this.editableInputs().forEach((target) =>
      target.removeEventListener("input", this.boundClearOnInput)
    );
    window.removeEventListener("beforeunload", this.boundBeforeUnload);
    document.removeEventListener("turbo:before-visit", this.boundBeforeVisit);
    if (this.wizardForm) {
      this.wizardForm.removeEventListener("turbo:submit-end", this.boundSubmitEnd);
    }
  }

  handleSubmitEnd(event) {
    if (event.detail?.success) this.clearDirty();
  }

  // GR-2566 — resync counter + empty state after rows are appended in place by
  // the bulk-add flow (which persists server-side, so nothing to mark dirty).
  refresh() {
    this.updateCounter();
    this.checkEmptyState();
  }

  markDirty() {
    this.dirty = true;
    if (this.hasUnsavedIndicatorTarget) {
      this.unsavedIndicatorTarget.classList.remove("d-none");
    }
  }

  clearDirty() {
    this.dirty = false;
    if (this.hasUnsavedIndicatorTarget) {
      this.unsavedIndicatorTarget.classList.add("d-none");
    }
  }

  handleBeforeUnload(event) {
    if (!this.dirty) return;
    event.preventDefault();
    event.returnValue = "";
  }

  handleBeforeVisit(event) {
    if (!this.dirty) return;
    if (!window.confirm("You have unsaved invitee changes. Leave anyway?")) {
      event.preventDefault();
    }
  }

  hasUnpersistedInviteeRows() {
    const rows = this.element.querySelectorAll(".invitee-row");
    return Array.from(rows).some((row) => {
      // JS-template rows never insert an [id] field; server-rendered rows
      // for unsaved (new) records render the [id] field but leave it blank.
      const idInput = row.querySelector('input[name$="[id]"]');
      return !idInput || !idInput.value;
    });
  }

  editableInputs() {
    const targets = [];
    if (this.hasEmailTarget) targets.push(this.emailTarget);
    if (this.hasFirstNameTarget) targets.push(this.firstNameTarget);
    if (this.hasLastNameTarget) targets.push(this.lastNameTarget);
    return targets;
  }

  clearOnInput(event) {
    this.clearValidationError(event.currentTarget);
  }

  /**
   * Handle auto-add from Azure AD lookup
   */
  handleAutoAdd(event) {
    const { email, firstName, lastName } = event.detail;
    if (email) {
      this.addInviteeDirectly(email, firstName || "", lastName || "");
    }
  }

  /**
   * Add invitee directly without using form fields (for auto-add)
   */
  addInviteeDirectly(email, firstName, lastName) {
    // Validate email format
    if (!this.isValidEmail(email)) {
      return;
    }

    // Check for duplicates
    const existingEmails = this.getExistingEmails();
    if (existingEmails.includes(email.toLowerCase())) {
      return;
    }

    // Create new invitee row
    this.createInviteeRow(email, firstName, lastName);

    // Remove empty state if present
    this.removeEmptyState();

    // Update counter
    this.updateCounter();

    this.markDirty();

    // Notify other controllers that an invitee was added
    this.element.dispatchEvent(
      new CustomEvent("invitee:added", { bubbles: true })
    );
  }

  /**
   * Add a new invitee to the list
   */
  addInvitee(event) {
    event.preventDefault();

    const email = this.emailTarget.value.trim();
    const firstName = this.hasFirstNameTarget ? this.firstNameTarget.value.trim() : "";
    const lastName = this.hasLastNameTarget ? this.lastNameTarget.value.trim() : "";

    if (!email) {
      this.emailTarget.focus();
      this.showValidationError(this.emailTarget, "Please enter an email address");
      return;
    }

    // Validate email format
    if (!this.isValidEmail(email)) {
      this.showValidationError(this.emailTarget, "Please enter a valid email address");
      return;
    }
    this.clearValidationError(this.emailTarget);

    // Validate first name
    if (!firstName && this.hasFirstNameTarget) {
      this.firstNameTarget.focus();
      this.showValidationError(this.firstNameTarget, "Please enter a first name");
      return;
    }
    if (this.hasFirstNameTarget) this.clearValidationError(this.firstNameTarget);

    // Validate last name
    if (!lastName && this.hasLastNameTarget) {
      this.lastNameTarget.focus();
      this.showValidationError(this.lastNameTarget, "Please enter a last name");
      return;
    }
    if (this.hasLastNameTarget) this.clearValidationError(this.lastNameTarget);

    // Check for duplicates
    const existingEmails = this.getExistingEmails();
    if (existingEmails.includes(email.toLowerCase())) {
      this.showValidationError(this.emailTarget, "This email has already been added");
      return;
    }

    // Create new invitee row
    this.createInviteeRow(email, firstName, lastName);

    // Clear form inputs
    this.emailTarget.value = "";
    if (this.hasFirstNameTarget) this.firstNameTarget.value = "";
    if (this.hasLastNameTarget) this.lastNameTarget.value = "";
    this.emailTarget.focus();

    // Notify other controllers (e.g., Azure AD lookup) that an invitee was added
    this.element.dispatchEvent(
      new CustomEvent("invitee:added", { bubbles: true })
    );

    // Remove empty state if present
    this.removeEmptyState();

    // Update counter
    this.updateCounter();

    this.markDirty();

    // Confirm the add to the user — the row inserts below the form so the
    // success isn't always in viewport.
    const displayName = [firstName, lastName].filter(Boolean).join(" ") || email;
    toastManager.show({
      message: `${displayName} added.`,
      variant: "success",
      icon: "check",
      autoCloseMs: 3000,
    });
  }

  /**
   * Show validation error on an input
   */
  showValidationError(input, message) {
    input.setCustomValidity(message);
    input.reportValidity();

    // Also add visual feedback
    const wrapper = input.closest(".FormControl") || input.parentElement;
    if (wrapper) {
      wrapper.classList.add("FormControl-error");
    }
  }

  /**
   * Clear validation error from an input
   */
  clearValidationError(input) {
    input.setCustomValidity("");
    const wrapper = input.closest(".FormControl") || input.parentElement;
    if (wrapper) {
      wrapper.classList.remove("FormControl-error");
    }
  }

  /**
   * Create a new invitee row element
   */
  createInviteeRow(email, firstName, lastName) {
    const template = document.getElementById("invitee-row-template");
    if (!template) return;

    const index = this.getNextIndex();
    const fullName = [firstName, lastName].filter(Boolean).join(" ");
    const prefix = `event_request[event_request_invitees_attributes][${index}]`;

    const fragment = template.content.cloneNode(true);
    const row = fragment.querySelector(".invitee-row");
    row.dataset.email = email.toLowerCase();

    // Populate name/email display
    const nameSpan = row.querySelector(".invitee-name");
    const emailSpan = row.querySelector(".invitee-email-display");

    if (fullName) {
      nameSpan.textContent = fullName;
      nameSpan.classList.add("invitee-email"); // used by getExistingEmails selector
      emailSpan.textContent = email;
      emailSpan.classList.add("f6", "color-fg-muted");
    } else {
      nameSpan.remove();
      emailSpan.textContent = email;
      emailSpan.classList.add("invitee-email", "text-semibold");
    }

    // Set hidden field names and values
    row.querySelectorAll("input[data-field]").forEach((input) => {
      const field = input.dataset.field;
      input.name = `${prefix}[${field}]`;
      if (field === "email") input.value = email;
      else if (field === "first_name") input.value = firstName;
      else if (field === "last_name") input.value = lastName;
    });

    // Add day assignment mode selector only for multi-day requests. GR-1758:
    // single-day requests have nothing to choose between, so the server partial
    // (_invitee_row) hides it on `size > 1` — mirror that here so rows added
    // dynamically (this JS path) don't reintroduce the selector.
    if (this.eventDaysValue.length > 1) {
      const toggleWrap = document.createElement("div");
      toggleWrap.dataset.controller = "day-assignment-toggle";

      // Select: All Days / Specific Days
      const select = document.createElement("select");
      select.className = "form-select form-select-sm";
      select.name = `${prefix}[day_assignment_mode]`;
      select.dataset.dayAssignmentToggleTarget = "select";
      select.dataset.action = "change->day-assignment-toggle#setVisibility";
      select.innerHTML = `<option value="all_days" selected>All Days</option><option value="specific_days">Specific Days</option>`;
      toggleWrap.appendChild(select);

      // Day checkboxes (hidden by default — all_days is default)
      const daysWrap = document.createElement("div");
      daysWrap.className = "d-flex flex-wrap gap-2 mt-2";
      daysWrap.dataset.dayAssignmentToggleTarget = "days";
      daysWrap.hidden = true;
      daysWrap.style.display = "none";

      this.eventDaysValue.forEach((day) => {
        const label = document.createElement("label");
        label.className = "d-flex flex-items-center gap-1 f6";
        const checkbox = document.createElement("input");
        checkbox.type = "checkbox";
        checkbox.name = `${prefix}[event_day_ids][]`;
        checkbox.value = day.id;
        checkbox.checked = true;
        label.appendChild(checkbox);
        label.appendChild(document.createTextNode(` ${day.label}`));
        daysWrap.appendChild(label);
      });

      toggleWrap.appendChild(daysWrap);
      row.appendChild(toggleWrap);
    }

    // Insert into the list container
    const container = this.element.querySelector(".invitees-list");
    if (container) {
      const blankslate = container.querySelector(".blankslate, .Blankslate");
      if (blankslate) {
        blankslate.remove();
      }
      container.appendChild(row);
    }
  }

  /**
   * Remove an invitee from the list (for new/unsaved invitees)
   */
  removeInvitee(event) {
    event.preventDefault();
    const row = event.target.closest(".invitee-row");
    if (row) {
      row.remove();
      this.updateCounter();
      this.checkEmptyState();
      this.markDirty();
    }
  }

  /**
   * Mark an existing invitee for destruction (for persisted records)
   */
  markForDestroy(event) {
    event.preventDefault();
    const button = event.target.closest("button");
    const row = button?.closest(".invitee-row");
    if (row) {
      // Look for the _destroy field by class or by name pattern
      const destroyField = row.querySelector(".destroy-field") ||
        row.querySelector('input[name*="[_destroy]"]');
      if (destroyField) {
        destroyField.value = "true";
        row.style.display = "none";
      } else {
        // No destroy field means it's a new (unsaved) invitee - just remove it
        row.remove();
      }
      this.updateCounter();
      this.checkEmptyState();
      this.markDirty();
    }
  }

  /**
   * Update the invitee counter display
   */
  updateCounter() {
    const counter = this.element.querySelector(".Counter");
    const visibleRows = this.element.querySelectorAll('.invitee-row:not([style*="display: none"])');
    const count = visibleRows.length;

    if (counter) {
      counter.textContent = count;
      counter.style.display = count > 0 ? "" : "none";
    }
  }

  /**
   * Check if we should show the empty state
   */
  checkEmptyState() {
    const container = this.element.querySelector(".invitees-list");
    const visibleRows = container?.querySelectorAll('.invitee-row:not([style*="display: none"])');

    if (container && (!visibleRows || visibleRows.length === 0)) {
      if (!container.querySelector(".blankslate")) {
        const template = document.getElementById("invitee-blankslate-template");
        if (template) {
          container.appendChild(template.content.cloneNode(true));
        }
      }
    } else {
      // Remove blankslate if there are visible rows
      const blankslate = container?.querySelector(".blankslate");
      if (blankslate) {
        blankslate.remove();
      }
    }
  }

  /**
   * Get existing email addresses (for duplicate checking)
   */
  getExistingEmails() {
    const rows = this.element.querySelectorAll(".invitee-row");
    return Array.from(rows)
      .filter((row) => row.style.display !== "none")
      .map(
        (row) =>
          row.dataset.email ||
          row.querySelector(".invitee-email")?.textContent?.toLowerCase()
      )
      .filter(Boolean);
  }

  /**
   * Get next index for new invitee attributes
   */
  getNextIndex() {
    const inputs = this.element.querySelectorAll(
      'input[name^="event_request[event_request_invitees_attributes]"]'
    );
    let maxIndex = -1;
    inputs.forEach((input) => {
      const match = input.name.match(/\[(\d+)\]/);
      if (match) {
        maxIndex = Math.max(maxIndex, parseInt(match[1], 10));
      }
    });
    return maxIndex + 1;
  }

  /**
   * Remove empty state message
   */
  removeEmptyState() {
    const container = this.element.querySelector(".invitees-list");
    if (container) {
      const blankslate = container.querySelector(".blankslate, .Blankslate");
      if (blankslate) {
        blankslate.remove();
      }
    }
  }

  /**
   * Validate email format
   */
  isValidEmail(email) {
    return /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email);
  }
}
